import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Badge,
  V2Button,
  V2ProgressBar,
  Icon,
  NoticeBanner,
  type V2BadgeTone,
  LoadingState,
  StatePanel,
} from '@sakhtar/design-system/react';
import { systemClock } from '@sakhtar/calendar';
import { apiGet, ApiError } from '../../lib/apiClient.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useCapabilities } from '../../lib/capabilities.js';
import { useAmountDisplay, useTenantSettings } from '../../lib/tenantSettings.js';
import { purchaseRequestsVisible, quotesVisible } from '../../lib/types/tenantSettings.js';
import { faDigits } from '../../lib/faDigits.js';
import type { FirstRunStep, HomeResponse } from '../../lib/types/shell.js';
import type { HomeDashboard } from '../../lib/types/home.js';
import type { OwnProfileResponse } from '../../lib/types/membership.js';
import type { SubscriptionResponse } from '../../lib/types/subscriptionSecurity.js';
import {
  setupStepView,
  SETUP_STATUS_LABEL,
  SETUP_STEP_LABEL,
  SETUP_STEP_KEYS,
  type SetupView,
} from '../../lib/types/setup.js';
import { HomeDashboardView, type Shortcut } from './HomeDashboard.js';
import { greeting } from './homeFormat.js';
import './home.css';

const TRIAL_BANNER_DISMISSED_KEY = 'sk-trial-banner-dismissed-until';

/**
 * UI_REFERENCE_SOURCE (primary, 2026-10-10): Sakhtar Cloud ERP v2.dc.html — frame `/t/kimia — خانه`
 *   v2 status: aligned — greeting header, subscription banner, KPI tiles,
 *   «نیازمند اقدام», «میان‌برها», «کارهای من», «فعالیت اخیر». Every section
 *   comes from `GET .../home/dashboard`, which omits (null) what the caller
 *   may not see; shortcuts are filtered by the caller's capabilities.
 *   Deviation: the «کالای زیر نقطهٔ سفارش» tile shows «کالای بدون موجودی
 *   قابل استفاده» — no reorder-point data exists yet, so nothing is invented.
 *
 * Legacy citation follows.
 */
/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Pilot P0.dc.html "TRIAL BANNER · HOME · THREE THRESHOLDS"
 * archetype: dismissible notice, three severity thresholds
 * status: KIT_CONFORMANT — end date intentionally omitted (product decision):
 *   `TrialViewModel` only returns `remainingDays`, no end-date field, and
 *   computing one from "today + N days" in apps/client would be exactly the
 *   ad-hoc date math AGENTS.md §8 rules out (Jalali/date arithmetic belongs
 *   in the calendar package only) — so this shows the day count only, never
 *   a fabricated end date.
 *
 * Role-gating (board: "فقط برای نقش مدیر سازمان و مدیر مالی") rides on the
 * real backend permission gate already enforced on this exact endpoint
 * (`tenant.subscription.manage`, apps/web/src/shell/navigation-config.ts) —
 * a 403/non-ready phase here means the viewer doesn't hold that permission,
 * so the banner is silently omitted rather than duplicating a role check
 * client-side (AGENTS.md §9: authorization is enforced in the backend).
 *
 * Board rule: > 7 days neutral+dismissible, 1–7 days warn+dismissible-until-
 * tomorrow, 0 days danger+not-dismissible, shown only on Home.
 */
function TrialBanner({ slug }: { slug: string }) {
  const base = tenantBasePath(slug);
  const result = useApiGet<SubscriptionResponse>(`${base}/api/v1/admin/subscription`, [slug]);
  const today = systemClock.today();
  const [dismissedUntil, setDismissedUntil] = useState<string | null>(() => {
    try {
      return localStorage.getItem(TRIAL_BANNER_DISMISSED_KEY);
    } catch {
      return null;
    }
  });

  if (
    result.status !== 'ready' ||
    result.data.phase !== 'ready' ||
    !result.data.subscription ||
    result.data.subscription.mode !== 'trial' ||
    !result.data.subscription.trial
  ) {
    return null;
  }

  const { remainingDays } = result.data.subscription.trial;
  const tone: 'info' | 'warn' | 'danger' =
    remainingDays > 7 ? 'info' : remainingDays > 0 ? 'warn' : 'danger';
  const dismissible = tone !== 'danger';

  if (dismissible && dismissedUntil === today) return null;

  function dismiss() {
    try {
      localStorage.setItem(TRIAL_BANNER_DISMISSED_KEY, today);
    } catch {
      // per-viewer convenience only — safe to no-op if storage is blocked
    }
    setDismissedUntil(today);
  }

  const title =
    tone === 'danger'
      ? 'اشتراک آزمایشی شما به پایان رسیده است'
      : `اشتراک آزمایشی شما ${faDigits(remainingDays)} روز دیگر به پایان می‌رسد`;
  const body =
    tone === 'info'
      ? 'پس از پایان اعتبار، فضای کاری به حالت «فقط‌خواندنی» می‌رود.'
      : tone === 'warn'
        ? 'ثبت سند و ویرایش متوقف می‌شود؛ داده‌ها حذف نمی‌شود.'
        : 'فضای کاری فقط‌خواندنی است. با انتخاب طرح، همان داده‌ها بلافاصله فعال می‌شود.';

  return (
    <div className={`home-banner home-wash home-banner--${tone}`} role="status">
      <span className="home-chip" aria-hidden="true">
        <Icon name={tone === 'info' ? 'subscription' : 'warn'} size="sm" />
      </span>
      <div className="home-banner__text">
        <div className="home-banner__title">{title}</div>
        <div className="home-banner__desc">{body}</div>
      </div>
      <Link to={`${base}/admin/subscription`}>
        <V2Button variant={tone === 'info' ? 'secondary' : 'primary'} size="sm">
          تمدید اشتراک
        </V2Button>
      </Link>
      {dismissible ? (
        <button type="button" className="sk-alert__close" aria-label="بستن" onClick={dismiss}>
          <Icon name="x" size="sm" />
        </button>
      ) : null}
    </div>
  );
}

const STATUS_TONE: Record<string, V2BadgeTone> = {
  complete: 'success',
  ready: 'warn',
  pending: 'neutral',
  blocked: 'danger',
};

const SETUP_STEP_TONE: Record<string, V2BadgeTone> = {
  COMPLETED: 'success',
  SKIPPED: 'neutral',
  IN_PROGRESS: 'warn',
  BLOCKED: 'neutral',
  NOT_STARTED: 'neutral',
};

/**
 * The tenant system-setup wizard's own checklist (`t/:slug/api/v1/setup`,
 * PR #137) — additive alongside the ERP-219 onboarding steps card below,
 * not a replacement of it (see `docs/agents/handoffs/
 * tenant-system-setup.md`'s own "intentionally out of scope" list). Shown
 * only while the run is not `COMPLETED`; a completed run has nothing left
 * to surface here.
 */
function SystemSetupCard({ slug }: { slug: string }) {
  const base = tenantBasePath(slug);
  const result = useApiGet<SetupView>(`${base}/api/v1/setup`, [slug]);

  if (result.status !== 'ready' || result.data.status === 'COMPLETED') return null;
  if (result.data.isTenantAdmin === false) return null;

  const view = result.data;
  const doneCount = SETUP_STEP_KEYS.filter((key) => {
    const status = setupStepView(view, key).status;
    return status === 'COMPLETED' || status === 'SKIPPED';
  }).length;
  const percent = Math.round((doneCount / SETUP_STEP_KEYS.length) * 100);

  return (
    <V2Card>
      <V2CardHeader
        title="راه‌اندازی اولیه سیستم"
        subtitle={`${doneCount.toLocaleString('fa-IR')} از ${SETUP_STEP_KEYS.length.toLocaleString('fa-IR')} مرحله انجام شده`}
        actions={
          <Link to={`${base}/setup`}>
            <V2Button variant="primary" size="sm">
              {view.status === 'NOT_STARTED' ? 'شروع راه‌اندازی' : 'ادامهٔ راه‌اندازی'}
            </V2Button>
          </Link>
        }
      />
      <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <V2ProgressBar percent={percent} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {SETUP_STEP_KEYS.map((key) => {
            const step = setupStepView(view, key);
            return (
              <div
                key={key}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <span style={{ fontSize: 13 }}>{SETUP_STEP_LABEL[key]}</span>
                <V2Badge tone={SETUP_STEP_TONE[step.status] ?? 'neutral'}>
                  {SETUP_STATUS_LABEL[step.status]}
                </V2Badge>
              </div>
            );
          })}
        </div>
      </div>
    </V2Card>
  );
}

/**
 * V2 home (frame `/t/kimia — خانه`): greeting + needs-action count, the
 * subscription banner, then the permission-filtered dashboard
 * (`HomeDashboardView`). Every figure comes from `GET .../home/dashboard`,
 * which returns `null` for any section the caller may not see and computes
 * record-scoped counts only at the caller's own scope — so each user sees
 * exactly the items their access allows, never a fake zero.
 *
 * The setup-wizard card and the ERP-219 onboarding checklist stay: they are
 * real first-run signals for a tenant still mid-setup.
 * state reference: ready · permission_denied · network_error
 */
export function HomePage() {
  const { slug } = useParams<{ slug: string }>();
  const [checklistDismissed, setChecklistDismissed] = useState<boolean>(() => {
    try {
      return slug ? localStorage.getItem(`sk-checklist-dismissed-${slug}`) === 'true' : false;
    } catch {
      return false;
    }
  });

  function dismissChecklist() {
    if (slug) {
      try {
        localStorage.setItem(`sk-checklist-dismissed-${slug}`, 'true');
      } catch {
        // storage blocked
      }
    }
    setChecklistDismissed(true);
  }

  const result = useApiGet<HomeResponse>(
    slug ? `${tenantBasePath(slug)}/api/v1/admin/home` : null,
    [slug],
  );
  const dashboard = useHomeDashboard(slug ? tenantBasePath(slug) : null);
  const shortcuts = useHomeShortcuts();
  const { unit } = useAmountDisplay();

  if (!slug) return null;
  const base = tenantBasePath(slug);
  const actionCount =
    dashboard.data?.actions.reduce((sum, action) => sum + action.count, 0) ?? null;

  if (result.status === 'loading') {
    return (
      <PageWrap slug={slug} actionCount={null} quickAdd={false}>
        <LoadingState />
      </PageWrap>
    );
  }

  if (result.status === 'error' || result.data.phase !== 'ready' || !result.data.steps) {
    const permissionDenied = result.status === 'ready' && result.data.phase === 'permission_denied';
    return (
      <PageWrap slug={slug} actionCount={null} quickAdd={false}>
        <StatePanel
          variant={permissionDenied ? 'permission_denied' : 'network_error'}
          title={permissionDenied ? 'دسترسی به خانه ندارید' : 'خانه قابل نمایش نیست'}
          description={permissionDenied ? undefined : 'دوباره تلاش کنید.'}
        />
      </PageWrap>
    );
  }

  const { steps } = result.data;
  const completed = steps.filter((s) => s.status === 'complete').length;
  const nextStep = steps.find((s) => s.status === 'ready');

  return (
    <PageWrap slug={slug} actionCount={actionCount} quickAdd={shortcuts.length > 0}>
      <SystemSetupCard slug={slug} />

      {dashboard.data ? (
        <HomeDashboardView
          base={base}
          data={dashboard.data}
          unit={unit}
          shortcuts={shortcuts}
          onChanged={dashboard.refresh}
        />
      ) : dashboard.error ? (
        <StatePanel
          variant="network_error"
          title="داشبورد خانه قابل نمایش نیست"
          description="دوباره تلاش کنید."
        />
      ) : (
        <LoadingState />
      )}

      {!checklistDismissed && steps.length > 0 ? (
        <StartupChecklistCard
          steps={steps}
          completed={completed}
          nextStep={nextStep}
          onDismiss={completed === steps.length ? dismissChecklist : undefined}
        />
      ) : null}
    </PageWrap>
  );
}

const DASHBOARD_REFRESH_MS = 60_000;

/**
 * `GET .../home/dashboard`, refreshed every minute and after a task is
 * completed. A refresh keeps the last good payload on screen (no loading
 * flash); only the first load shows a loading/error state.
 */
function useHomeDashboard(base: string | null): {
  readonly data: HomeDashboard | null;
  readonly error: ApiError | null;
  readonly refresh: () => void;
} {
  const [data, setData] = useState<HomeDashboard | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [nonce, setNonce] = useState(0);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!base) return;
    let cancelled = false;
    apiGet<HomeDashboard>(`${base}/api/v1/home/dashboard`)
      .then((payload) => {
        if (cancelled) return;
        setData(payload);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? err
            : new ApiError(0, {
                error: { code: 'INTERNAL', message: 'Network error', requestId: '' },
              }),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [base, nonce]);

  useEffect(() => {
    if (!base) return;
    const timer = window.setInterval(refresh, DASHBOARD_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [base, refresh]);

  return { data, error, refresh };
}

/**
 * «میان‌برها» — each entry is shown only when the caller holds its create
 * permission (DENY-first `useCapabilities`) and the tenant has that flow
 * switched on. The backend still enforces every create; this only avoids
 * offering a button that would 403.
 */
function useHomeShortcuts(): readonly Shortcut[] {
  const caps = useCapabilities();
  const settings = useTenantSettings();
  return useMemo(() => {
    const all: { readonly shortcut: Shortcut; readonly allowed: boolean }[] = [
      {
        shortcut: {
          id: 'quote',
          label: 'پیش‌فاکتور جدید',
          href: '/crm/quotes',
          icon: 'invoice',
          module: 'crm',
        },
        allowed: quotesVisible(settings) && caps.hasCapability('sales.quote.create'),
      },
      {
        shortcut: {
          id: 'order',
          label: 'سفارش فروش جدید',
          href: '/crm/orders',
          icon: 'sales',
          module: 'crm',
        },
        allowed: caps.hasCapability('sales.order.create'),
      },
      {
        shortcut: {
          id: 'party',
          label: 'طرف حساب جدید',
          href: '/master-data/parties/new',
          icon: 'building',
          module: undefined,
        },
        allowed: caps.hasCapability('master.party.create'),
      },
      {
        shortcut: {
          id: 'journal',
          label: 'سند حسابداری جدید',
          href: '/finance/journals',
          icon: 'finance',
          module: 'finance',
        },
        allowed: caps.hasCapability('accounting.journal-entry.create'),
      },
      {
        shortcut: {
          id: 'purchase-request',
          label: 'درخواست خرید جدید',
          href: '/procurement/requests',
          icon: 'cart',
          module: 'procurement',
        },
        allowed:
          purchaseRequestsVisible(settings) && caps.hasCapability('procurement.request.create'),
      },
      {
        shortcut: {
          id: 'import',
          label: 'ورود داده از اکسل',
          href: '/data',
          icon: 'upload',
          module: undefined,
          navigate: true,
        },
        allowed: caps.isTenantAdmin,
      },
    ];
    return all.filter((entry) => entry.allowed).map((entry) => entry.shortcut);
  }, [caps, settings]);
}

/**
 * C8's "شروع کار با ساختار" — one card, every real onboarding step as a
 * row (done vs. not, per the board's own binary), with an "انجام بده" CTA
 * only on the one step that's genuinely actionable next (`status ===
 * 'ready'`) — a `pending`/`blocked` step still shows its real tone badge
 * (data this endpoint already returns) rather than collapsing to the
 * board's plain open circle, since that's real signal, not decoration.
 * Replaces the previous progress-bar card + one-`V2Card`-per-step stack
 * with a single consolidated card; `SystemSetupCard` above it is a
 * different, still-separate real widget (the setup *wizard*'s own step
 * statuses) and is untouched.
 *
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Onboarding Team Desktop.html §C8 "چک‌لیست شروع در Home"
 * archetype: single bordered card, header count + divided rows (circle +
 *   label + trailing action), footer CTA + note
 * status: KIT_CONFORMANT — every row and count is `HomeResponse.steps`
 *   as-is; nothing here is fabricated.
 */
function StartupChecklistCard({
  steps,
  completed,
  nextStep,
  onDismiss,
}: {
  steps: readonly FirstRunStep[];
  completed: number;
  nextStep: FirstRunStep | undefined;
  onDismiss?: (() => void) | undefined;
}) {
  return (
    <V2Card>
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--sk-divider)',
          background: 'var(--sk-bg-canvas)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 600 }}>شروع کار با ساختار</span>
        <span style={{ marginInlineStart: 'auto', fontSize: 12, color: 'var(--sk-fg-muted)' }}>
          {completed.toLocaleString('fa-IR')} از {steps.length.toLocaleString('fa-IR')} انجام شده
        </span>
        {onDismiss ? (
          <button
            type="button"
            className="sk-alert__close"
            aria-label="بستن"
            onClick={onDismiss}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--sk-fg-muted)',
              display: 'flex',
              alignItems: 'center',
              padding: 2,
            }}
          >
            <Icon name="x" size="sm" />
          </button>
        ) : null}
      </div>
      <div style={{ padding: '8px 20px 4px' }}>
        {steps.map((step) => (
          <div
            key={step.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 0',
              borderBottom: '1px solid var(--sk-divider)',
            }}
          >
            {step.status === 'complete' ? (
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 10,
                  background: 'var(--sk-success)',
                  color: '#fff',
                  display: 'grid',
                  placeContent: 'center',
                  flex: '0 0 auto',
                }}
              >
                <Icon name="check" size="sm" />
              </span>
            ) : (
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 10,
                  border: '1.5px solid var(--sk-border-strong)',
                  flex: '0 0 auto',
                }}
              />
            )}
            <span
              style={{
                fontSize: 13.5,
                flex: 1,
                minWidth: 0,
                color: step.status === 'complete' ? 'var(--sk-fg-muted)' : 'var(--sk-fg)',
              }}
            >
              {step.label}
            </span>
            {step.status !== 'complete' ? (
              <V2Badge tone={STATUS_TONE[step.status] ?? 'neutral'}>{step.statusLabel}</V2Badge>
            ) : null}
            {step.status === 'ready' ? (
              <V2Button
                variant="ghost"
                size="sm"
                onClick={() => (window.location.href = step.href)}
              >
                انجام بده
              </V2Button>
            ) : null}
          </div>
        ))}
      </div>
      <div
        style={{
          padding: '14px 20px',
          background: 'var(--sk-bg-canvas)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        {nextStep ? (
          <V2Button
            variant="primary"
            size="sm"
            onClick={() => (window.location.href = nextStep.href)}
          >
            ادامه راه‌اندازی
          </V2Button>
        ) : null}
        <span style={{ fontSize: 12, color: 'var(--sk-fg-subtle)' }}>
          {completed === steps.length
            ? 'همهٔ مراحل راه‌اندازی با موفقیت انجام شد.'
            : 'این فهرست عملیاتی است، نه ویزارد دوباره.'}
        </span>
      </div>
    </V2Card>
  );
}

function PageWrap({
  slug,
  actionCount,
  quickAdd,
  children,
}: {
  slug: string;
  actionCount: number | null;
  quickAdd: boolean;
  children: ReactNode;
}) {
  const base = tenantBasePath(slug);
  const setup = useApiGet<SetupView>(`${base}/api/v1/setup`, [slug]);
  const me = useApiGet<OwnProfileResponse>(`${base}/api/v1/me`, [slug]);
  const showIncompleteBanner =
    setup.status === 'ready' &&
    setup.data.isTenantAdmin === false &&
    setup.data.status !== 'COMPLETED' &&
    setup.data.status !== 'SKIPPED';
  const firstName = me.status === 'ready' ? me.data.firstName?.trim() : undefined;

  return (
    <div className="home">
      <div className="home-head">
        <div className="home-head__title">
          <span className="sk-module-mark" aria-hidden="true">
            <Icon name="home" size="md" />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="home-head__crumb">خانه</div>
            <h1>{firstName ? `${greeting()}، ${firstName}` : greeting()}</h1>
            {actionCount !== null ? (
              <div className="home-head__sub">
                {actionCount > 0
                  ? `${faDigits(actionCount)} مورد امروز نیازمند اقدام شماست.`
                  : 'امروز مورد معوقی برای شما نیست.'}
              </div>
            ) : null}
          </div>
        </div>
        {quickAdd ? (
          <V2Button
            className="home-head__quick"
            variant="primary"
            size="sm"
            onClick={() =>
              document
                .getElementById('home-shortcuts')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          >
            <Icon name="plus" size="sm" />
            افزودن سریع
          </V2Button>
        ) : null}
      </div>
      {showIncompleteBanner ? (
        <NoticeBanner tone="info" style={{ marginBlockEnd: 0 }}>
          راه‌اندازی سازمان هنوز کامل نشده است. مدیر سازمان باید آن را تکمیل کند.
        </NoticeBanner>
      ) : null}
      <TrialBanner slug={slug} />
      {children}
    </div>
  );
}
